import {
  ID,
  ListApiResponse,
  unwrapApiErrorDetails
} from '@ianesthesie-kit/services';
import { parseDateOrNull } from '@ianesthesie-kit/core';
import axios from 'axios';
import {
  Automation,
  AutomationActionExecutionLog
} from '../automation/automation.model';
import { sub } from 'date-fns';

export class AutomationService {
  private static INSTANCE_BY_ID: AutomationService;

  private readonly patientId?: ID;
  private readonly url;

  private lastDataQueriedAt: number;

  private constructor(patientId: ID) {
    this.url = `/bff/doctor/patient/${patientId}/surgical-event/active`;
    this.patientId = patientId;
    this.lastDataQueriedAt = Date.now();
  }

  public static fetchHospitalAutomations(): Promise<Record<ID, Automation>> {
    const automationsById: Record<string, Automation> = {};
    return axios
      .get<ListApiResponse<Automation>>('/bff/automation')
      .then((resp) =>
        resp.data.data.reduce((acc, automation) => {
          automationsById[automation.id] = automation;
          return automationsById;
        }, automationsById)
      );
  }

  public static getInstance(patientId: ID): AutomationService {
    if (this.shouldCreateNewInstance(this.INSTANCE_BY_ID, patientId)) {
      this.INSTANCE_BY_ID = new AutomationService(patientId);
    }
    return this.INSTANCE_BY_ID;
  }

  protected static shouldCreateNewInstance(
    instance: AutomationService,
    patientId: ID
  ): boolean {
    if (!patientId) {
      throw new Error('Patient id must be provided');
    }
    return !instance || instance.patientId !== patientId;
  }

  public fetchLatestAutomationExecutions(): Promise<{
    data: AutomationActionExecutionLog[];
  }> {
    const qs = `?executed-after=${this.lastDataQueriedAt}`;
    return axios
      .get<ListApiResponse<AutomationActionExecutionLog>>(
        `${this.url}/automation-execution${qs}`
      )
      .then((resp) => {
        const data = resp.data.data;
        this.updateLastQueriedTime(data);
        return {
          data: data.sort(this.sort)
        };
      })
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }

  public fetchAutomationExecutionsForLastMinutes(
    minutes: number
  ): Promise<{ data: AutomationActionExecutionLog[] }> {
    return this.fetchAutomationExecutionsSince(
      sub(Date.now(), { minutes }).toISOString()
    );
  }

  public fetchAutomationExecutionsSince(
    minExecutionDateTime: string
  ): Promise<{ data: AutomationActionExecutionLog[] }> {
    const date = parseDateOrNull(minExecutionDateTime);
    if (!date) {
      throw new Error(`Not a valid date: ${minExecutionDateTime}`);
    }
    const qs = `?executed-after=${date.toISOString()}`;
    return axios
      .get<ListApiResponse<AutomationActionExecutionLog>>(
        `${this.url}/automation-execution${qs}`
      )
      .then((resp) => ({
        data: resp.data.data.sort(this.sort)
      }))
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }

  private sort(
    a: AutomationActionExecutionLog,
    b: AutomationActionExecutionLog
  ): number {
    return b.executedAt.localeCompare(a.executedAt);
  }

  private updateLastQueriedTime(data: AutomationActionExecutionLog[]) {
    const mostResentDateStr = data
      .map((log) => log.executedAt)
      .sort((a, b) => b.localeCompare(a))[0];
    const d = parseDateOrNull(mostResentDateStr);
    if (d) {
      this.lastDataQueriedAt = +d;
    }
  }
}
